// The create panel. Everything about the form itself lives in TemplateForm;
// this is the shell around it — header, save, cancel, and turning a rejected
// save back into per-field messages.

import { useState } from 'react'
import { ActionButton } from '~/team_tools/components/ActionButton'
import { ConfirmBar } from '~/team_tools/components/ConfirmBar'
import { useNotification } from '~/team_tools/components/NotificationProvider'
import { TemplateFormFields } from './TemplateForm'
import {
    TemplateFormState,
    blankForm,
    parseFieldErrors,
    payloadOf,
    previewName,
} from './templateFormState'
import { Options, saveTemplate } from './templatesApi'

export const TemplateEditor = ({
    options,
    canEdit,
    seed,
    onSaved,
    onCancel,
}: {
    options: Options
    canEdit: boolean
    /** Pre-filled state, for duplicating an existing template. */
    seed?: TemplateFormState | null
    onSaved: () => void
    onCancel: () => void
}) => {
    const { addNotification } = useNotification()
    const [form, setForm] = useState(seed ?? blankForm())
    const [errors, setErrors] = useState<Record<string, string>>({})
    const [saving, setSaving] = useState(false)
    const [confirmCancel, setConfirmCancel] = useState(false)

    // Compared against what the panel opened with, not a blank form: a
    // duplicate starts pre-filled and would otherwise always look unsaved.
    const opened = JSON.stringify(seed ?? blankForm())
    const touched = JSON.stringify(form) !== opened

    // Checked before the request so the obvious mistakes are answered without a
    // round trip. The backend validates all of this again — this is a courtesy,
    // not the enforcement.
    const preview = previewName(form.server_name_template, {
        mapName: options.maps.find((m) => m.id === Number(form.map_id))?.name,
        fee: form.fee,
    })
    const localErrors: Record<string, string> = {}
    if (!form.name.trim()) localErrors.name = 'Give the template a name.'
    if (!form.deploy_slot_regions.length)
        localErrors.deploy_slot_regions = 'Pick at least one region to deploy into.'
    if (preview.unknown.length)
        localErrors.server_name_template = `Unknown placeholder(s): ${preview.unknown.join(', ')}`
    if (preview.tooLong) localErrors.server_name_template = 'The rendered name is too long.'

    const save = async () => {
        if (Object.keys(localErrors).length) {
            setErrors(localErrors)
            addNotification('Some fields need fixing before this can be saved', 'error')
            return
        }
        setSaving(true)
        try {
            await saveTemplate(null, payloadOf(form))
            addNotification(`Template "${form.name}" created`, 'success')
            onSaved()
        } catch (err: any) {
            // Django's errors arrive flattened into one string. Anchoring them
            // to their fields is the difference between a fixable form and a
            // three-second toast nobody can re-read.
            const fields = parseFieldErrors(err.message)
            setErrors(fields)
            addNotification(
                Object.keys(fields).length ? 'Could not save. See the fields marked below' : err.message,
                'error'
            )
        } finally {
            setSaving(false)
        }
    }

    const cancel = () => (touched ? setConfirmCancel(true) : onCancel())

    return (
        <div className='flex flex-col gap-4 border border-white/20 rounded p-4 text-sm'>
            <header className='flex flex-row items-center justify-between'>
                <h3 className='uppercase tracking-widest text-xs text-white/60'>
                    {seed ? 'Duplicate template' : 'New template'}
                </h3>
                {!canEdit && (
                    <span className='text-[11px] text-amber-400'>
                        Read-only. Authoring templates requires the executive role.
                    </span>
                )}
            </header>

            <TemplateFormFields
                form={form}
                setForm={setForm}
                options={options}
                errors={errors}
                disabled={!canEdit}
            />

            {confirmCancel ? (
                <ConfirmBar
                    message='Discard this template?'
                    detail='Nothing has been saved yet, so everything typed here is lost.'
                    confirmLabel='Discard'
                    onConfirm={onCancel}
                    onCancel={() => setConfirmCancel(false)}
                />
            ) : (
                <div className='flex flex-row items-center gap-3'>
                    <ActionButton onClick={save} disabled={!canEdit || saving} className='text-amber-400'>
                        {saving ? 'creating…' : 'Create template'}
                    </ActionButton>
                    <ActionButton onClick={async () => cancel()}>Cancel</ActionButton>
                </div>
            )}
        </div>
    )
}
